import {
  MailDelivery,
  MailedNoticeDocumentType,
  ShippingTier,
  getMailBillingUnit,
  MailBillingUnit
} from '../../types/mail';
import { ColumnService } from '../../services/directory';
import { getErrorReporter } from '../../utils/errors';
import { ResponseOrError, wrapError, wrapSuccess } from '../../types/responses';
import { InternalServerError } from '../../errors/ColumnErrors';
import {
  NoticeAddOnPricing,
  NoticeAddOnPricingConfig,
  NoticeAddOnPricingContext,
  makeUnitAddOnPricing,
  DEFAULT_RON_AFFIDAVIT_FEE_IN_CENTS,
  DEFAULT_WET_SIG_AFFIDAVIT_FEE_IN_CENTS,
  DEFAULT_EXPRESS_SHIPPING_FEE_IN_CENTS,
  DEFAULT_RUSH_SHIPPING_FEE_IN_CENTS,
  DEFAULT_GROUND_SHIPPING_FEE_IN_CENTS
} from './types';

function getShippingTierRate(
  tier: ShippingTier,
  config: NoticeAddOnPricingConfig
): number {
  if (tier === ShippingTier.EXPRESS) {
    return (
      config.expressShippingFeeInCents ?? DEFAULT_EXPRESS_SHIPPING_FEE_IN_CENTS
    );
  }
  if (tier === ShippingTier.RUSH) {
    return config.rushShippingFeeInCents ?? DEFAULT_RUSH_SHIPPING_FEE_IN_CENTS;
  }
  return (
    config.groundShippingFeeInCents ?? DEFAULT_GROUND_SHIPPING_FEE_IN_CENTS
  );
}

/**
 * Computes per-copy ground shipping for standard affidavit copies.
 * Returns undefined when there are no copies.
 */
export function computeStandardAffidavitShipping(
  standardRecords: MailDelivery[],
  config: NoticeAddOnPricingConfig
): NoticeAddOnPricing['standardAffidavitShipping'] {
  const totalCopies = standardRecords.reduce(
    (sum, m) => sum + (m.copies ?? 0),
    0
  );
  if (totalCopies === 0) {
    return undefined;
  }
  const groundRate =
    config.groundShippingFeeInCents ?? DEFAULT_GROUND_SHIPPING_FEE_IN_CENTS;
  return makeUnitAddOnPricing(groundRate, totalCopies);
}

/**
 * Computes the RON affidavit fee and per-copy ground shipping. When the paper is
 * in the AA program (affidavits managed by Column), the RON affidavit is included
 * and only the mailed copies (ground shipping) are charged.
 */
export function computeRonAffidavitPricing(
  ronRecords: MailDelivery[],
  config: NoticeAddOnPricingConfig,
  context: NoticeAddOnPricingContext
): Pick<NoticeAddOnPricing, 'ronAffidavit' | 'ronAffidavitShipping'> {
  const result: Pick<
    NoticeAddOnPricing,
    'ronAffidavit' | 'ronAffidavitShipping'
  > = {};

  // In the AA program the RON affidavit is included; charge only mailed copies.
  if (!context.affidavitsManagedByColumn) {
    result.ronAffidavit = {
      totalInCents:
        config.ronAffidavitFeeInCents ?? DEFAULT_RON_AFFIDAVIT_FEE_IN_CENTS
    };
  }

  const totalCopies = ronRecords.reduce((sum, m) => sum + (m.copies ?? 0), 0);
  if (totalCopies > 0) {
    result.ronAffidavitShipping = makeUnitAddOnPricing(
      config.groundShippingFeeInCents ?? DEFAULT_GROUND_SHIPPING_FEE_IN_CENTS,
      totalCopies
    );
  }

  return result;
}

/**
 * Computes unit-priced wet-sig affidavit fee and per-address shipping.
 * Returns an error if wet-sig records have inconsistent shipping tiers, as
 * silently applying the wrong tier would mis-price the order.
 */
export function computeWetSigAffidavitPricing(
  wetSigRecords: MailDelivery[],
  config: NoticeAddOnPricingConfig,
  context: NoticeAddOnPricingContext
): ResponseOrError<
  Pick<NoticeAddOnPricing, 'wetSigAffidavit' | 'wetSigAffidavitShipping'>
> {
  const result: Pick<
    NoticeAddOnPricing,
    'wetSigAffidavit' | 'wetSigAffidavitShipping'
  > = {};

  const totalCopies = wetSigRecords.reduce(
    (sum, m) => sum + (m.copies ?? 0),
    0
  );
  // When the publisher covers the first wet-sig affidavit, the first copy is free.
  const chargeableCopies = context.firstWetSigAffidavitFree
    ? Math.max(totalCopies - 1, 0)
    : totalCopies;
  if (chargeableCopies > 0) {
    result.wetSigAffidavit = makeUnitAddOnPricing(
      config.wetSigAffidavitFeeInCents ??
        DEFAULT_WET_SIG_AFFIDAVIT_FEE_IN_CENTS,
      chargeableCopies
    );
  }

  const firstTier = wetSigRecords[0].shippingTier ?? ShippingTier.GROUND;
  const tiersAreConsistent = wetSigRecords.every(
    r => (r.shippingTier ?? ShippingTier.GROUND) === firstTier
  );
  if (!tiersAreConsistent) {
    const err = new InternalServerError(
      'Inconsistent shipping tiers across wet-sig affidavit records'
    );
    getErrorReporter().logAndCaptureCriticalError(
      ColumnService.PAYMENTS,
      err,
      'Mixed shipping tiers detected in computeWetSigAffidavitPricing',
      {}
    );
    return wrapError(err);
  }

  const billingUnit = getMailBillingUnit(
    MailedNoticeDocumentType.WET_SIG_AFFIDAVIT
  );
  const shippingTier = firstTier;
  const tierRate = getShippingTierRate(shippingTier, config);

  if (billingUnit === MailBillingUnit.PER_ADDRESS) {
    // Each recipient row is a separately mailed affidavit (its own label), so
    // bill per row rather than per unique address — an additional copy sent to
    // the same address as the included affidavit is still its own shipment.
    const shipmentCount = wetSigRecords.length;
    // The included copy is the first row; the publisher covers its ground
    // shipment, so the first shipment is free on ground. Express/rush is never
    // covered, so every shipment is charged once the order is upgraded.
    const chargeableShipmentCount =
      context.firstWetSigAffidavitFree && shippingTier === ShippingTier.GROUND
        ? Math.max(shipmentCount - 1, 0)
        : shipmentCount;
    if (chargeableShipmentCount > 0) {
      result.wetSigAffidavitShipping = {
        shippingTier,
        qty: chargeableShipmentCount,
        unitPriceInCents: tierRate,
        totalInCents: chargeableShipmentCount * tierRate
      };
    }
  }

  return wrapSuccess(result);
}
